import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityId,
} from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';
import { selectShiftRelationshipEntity } from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';

import { captureHydrationException } from '../helpers';
import { selectAllJobs, selectJobRelationshipId } from '../jobs/slice';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import { User, UserSerializedEntity, UsersState } from './types';

export const usersAdapter = createEntityAdapter<User>();

export const initialState: UsersState = {
  ...usersAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action.payload?.included) {
        try {
          const users = (action?.payload?.included as UserSerializedEntity[])
            .filter((entity: UserSerializedEntity) => entity.type === 'user')
            .map((entity: UserSerializedEntity) => ({
              id: entity.id,
              type: entity.type,
              firstName: entity.attributes.firstName,
              lastName: entity.attributes.lastName,
              avatar: entity.attributes.avatar,
              jobs: entity.relationships?.jobs?.data,
            }));
          usersAdapter.setAll(state, users);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });
  },
});

export const reducer = slice.reducer;

const getScheduleBuilderState = (state: RootState) =>
  state.get(PARENT_SLICE_NAME);

export const {
  selectById: selectUserById,
  selectIds: selectUserIds,
  selectEntities: selectUserEntities,
  selectAll: selectAllUsers,
} = usersAdapter.getSelectors<RootState>(
  state => getScheduleBuilderState(state)[SLICE_NAME] || initialState
);

export const isUserId = (
  userId: EntityId | EntityId[] | undefined
): userId is EntityId => Boolean(userId && !Array.isArray(userId));

export const selectUserIdFromJobId = createCachedSelector(
  (state: RootState, jobId: EntityId) => {
    const userId = selectJobRelationshipId({
      state,
      relationship: 'user',
      id: jobId,
    });
    return userId;
  },
  userId => userId
)((_, jobId: EntityId) => `selectUserIdFromJobId-${jobId}`);

export const selectUserAvatarUrlByJobId = createCachedSelector(
  (state: RootState, jobId: EntityId) => {
    const userId = selectUserIdFromJobId(state, jobId);
    return isUserId(userId) ? selectUserById(state, userId)?.avatar : undefined;
  },
  avatar => avatar
)((_, jobId: EntityId) => `selectUserAvatarUrlByJobId-${jobId}`);

export const selectUserFirstName = (state: RootState, userId: EntityId) =>
  isUserId(userId) ? selectUserById(state, userId)?.firstName : '';

export const selectUserFirstNameByJobId = createCachedSelector(
  (state: RootState, jobId: EntityId) => {
    const userId = selectUserIdFromJobId(state, jobId);
    return isUserId(userId) ? selectUserFirstName(state, userId) : '';
  },
  (firstName: string | undefined) => firstName || ''
)((_, jobId: EntityId) => `selectUserFirstNameByJobId-${jobId}`);

export const selectUserAttribute = <T extends keyof User>({
  state,
  attribute,
  userId,
}: {
  state: RootState;
  attribute: T;
  userId: string;
}): User[T] | undefined => {
  const user = selectUserById(state, userId);
  return user ? user[attribute] : undefined;
};

export const selectUserFullName = createCachedSelector(
  (state: RootState, userId: EntityId) => selectUserById(state, userId),
  user => {
    if (!user) return '';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
)((_, userId: EntityId) => `selectUserFullName-${userId}`);

export const selectFirstNameWithLastInitial = createCachedSelector(
  (state: RootState, userId: EntityId) => selectUserById(state, userId),
  user => {
    if (!user) return '';
    const lastName = user.lastName ? `${user.lastName.charAt(0)}.` : '';
    return `${user.firstName || ''} ${lastName}`.trim();
  }
)((_, userId: EntityId) => `selectUserNameWithLastInitial-${userId}`);

export const selectUserNameWithLastInitial = createCachedSelector(
  (state: RootState, jobId: EntityId) => {
    const userId = selectUserIdFromJobId(state, jobId);
    return isUserId(userId)
      ? selectFirstNameWithLastInitial(state, userId)
      : '';
  },
  name => name
)((_, jobId: EntityId) => `selectUserNameWithLastInitial-${jobId}`);

export const selectUserNameByShiftId = createCachedSelector(
  (state: RootState, shiftId: EntityId) => {
    const jobId = selectShiftRelationshipEntity({
      state,
      relationship: 'owner',
      id: shiftId,
    })?.id;

    if (!jobId) return '';
    const userId = selectJobRelationshipId({
      state,
      relationship: 'user',
      id: jobId,
    });

    return isUserId(userId) ? selectUserFullName(state, userId) : '';
  },
  name => name
)((_, shiftId: EntityId) => `selectUserNameByShiftId-${shiftId}`);

export const selectFullNameByJobId = createCachedSelector(
  (state: RootState, jobId: EntityId) => {
    const userId = selectUserIdFromJobId(state, jobId);
    return isUserId(userId) ? selectUserFullName(state, userId) : '';
  },
  name => name
)((_, jobId: EntityId) => `selectFullNameByJobId-${jobId}`);

export const selectUserIdUserNameMap = createSelector(
  [state => selectAllUsers(state)],
  users =>
    users.reduce((memo, user) => {
      memo[user.id] = {
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        firstName: user.firstName,
        lastName: user.lastName,
      };
      return memo;
    }, {} as Record<string, { fullName: string; firstName: string; lastName: string }>)
);

export const selectUserJobs = createCachedSelector(
  [
    (state: RootState) => selectAllJobs(state),
    (_: unknown, userId: EntityId) => userId,
  ],
  (allJobs, userId) => {
    const userJobs = allJobs.filter(job => job.user?.id === userId);
    return userJobs;
  }
)((_, userId: EntityId) => `selectUserJobs-${userId}`);

/**
 * this returns a object with user id as key and array of job ids as value
 *
 * where 1 is a user id and ['1', '2', '3'] is an array of job ids
 * {
 *  '1': ['1', '2', '3'],
 *  '2': ['4', '5', '6'],
 * }
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - breaks inference
export const selectUserIdJobIdMap = createSelector(
  selectAllUsers,
  (state: RootState) => selectAllJobs(state),
  (users, jobs) => {
    const userJobsMap: Record<string, EntityId[]> = {};

    users?.forEach(user => {
      userJobsMap[user.id] = [];
    });

    // Build user->jobs mapping from jobs->user relationships
    jobs?.forEach(job => {
      const userId = job.user?.id;
      if (userId && userJobsMap[userId]) {
        userJobsMap[userId].push(job.id);
      }
    });

    return userJobsMap;
  }
);

export const selectJobIdsForUser = createCachedSelector(
  (state: RootState, props: { jobId: EntityId }) => {
    const userId = selectJobRelationshipId({
      state,
      relationship: 'user',
      id: props.jobId,
    });

    const jobs =
      userId && !Array.isArray(userId) ? selectUserJobs(state, userId) : [];
    return jobs.map(job => job.id).join(',');
  },
  jobs => (jobs.length ? jobs.split(',') : [])
)((_, props: { jobId: EntityId }) => `selectJobIdsForUser-${props.jobId}`);
